import React, { useState } from 'react';
import { useProject } from '../contexts/ProjectContext';
import toast from 'react-hot-toast';

interface ProjectAssignmentSelectProps {
  currentProjectId?: string | null;
  onAssign: (projectId: string | null) => Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** When true, shows a placeholder "Set Project" instead of reflecting currentProjectId */
  bulk?: boolean;
}

const PLACEHOLDER = '__placeholder__';
const CHECK = '\u2713 ';

const ProjectAssignmentSelect: React.FC<ProjectAssignmentSelectProps> = ({
  currentProjectId,
  onAssign,
  disabled = false,
  size = 'sm',
  bulk = false,
}) => {
  const { projects, hasProjectAccess } = useProject();
  const [isAssigning, setIsAssigning] = useState(false);

  if (!hasProjectAccess || projects.length === 0) {
    return null;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === PLACEHOLDER) return;
    const projectId = value === '' ? null : value;

    setIsAssigning(true);
    try {
      await onAssign(projectId);
      const projectName = projectId
        ? projects.find(p => p.id === projectId)?.name ?? 'project'
        : 'Unassigned';
      toast.success(`Project set to ${projectName}`);
    } catch {
      toast.error('Failed to assign project');
    } finally {
      setIsAssigning(false);
      // Reset bulk select back to placeholder after assignment
      if (bulk) {
        e.target.value = PLACEHOLDER;
      }
    }
  };

  const sizeClasses = size === 'sm'
    ? 'h-9 pl-3 pr-9 text-xs'
    : 'h-10 pl-3.5 pr-10 text-sm';

  const baseClasses = bulk
    ? 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
    : 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100';

  const selectValue = bulk ? PLACEHOLDER : (currentProjectId ?? '');

  return (
    <div className="relative inline-flex shrink-0">
      <select
        value={selectValue}
        onChange={handleChange}
        disabled={disabled || isAssigning}
        className={`${sizeClasses} w-full appearance-none rounded-lg border font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${baseClasses}`}
      >
        {bulk && (
          <option value={PLACEHOLDER} disabled hidden>
            Project
          </option>
        )}
        <option value="">
          {!bulk && !currentProjectId ? CHECK : ''}No Project
        </option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {!bulk && currentProjectId === project.id ? CHECK : ''}{project.name}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-current opacity-70">
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
};

export default ProjectAssignmentSelect;
